import { useCallback, useMemo } from "react";

import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";

import { CASES_FOR_ENTITY_DEFAULT_SORT } from "src/cases/queries/cases";
import {
  parseSortParam,
  sortingStateToSortParam,
} from "src/cases/queues/hooks/filterParamUtils";

export const CASES_SORT_PARAM = "cases-sort";

// Persists the entity Cases table sort in the URL so it survives reloads, is
// shareable, and can be carried across tab navigation, mirroring how the
// sidepane state is stored on this page.
export const useCasesSortParam = (): [
  SortingState,
  OnChangeFn<SortingState>,
] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam =
    searchParams.get(CASES_SORT_PARAM) ?? CASES_FOR_ENTITY_DEFAULT_SORT;

  const sorting = useMemo<SortingState>(
    () => [parseSortParam(sortParam)],
    [sortParam],
  );

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;
      const newSortParam =
        sortingStateToSortParam(newSorting) ?? CASES_FOR_ENTITY_DEFAULT_SORT;
      setSearchParams(
        (prev) => {
          prev.set(CASES_SORT_PARAM, newSortParam);
          return prev;
        },
        { replace: true },
      );
    },
    [sorting, setSearchParams],
  );

  return [sorting, onSortingChange];
};
